import { useQuery } from "@tanstack/react-query"
import { Card, Divider, Typography } from "antd"
import type React from "react"

const { Text } = Typography
import { useEffect, useState } from "react"
import VocabularySearchForm from "./components/VocabularySearchForm"
import VocabularyTable from "./components/VocabularyTable"
import VocabularyTableActions from "./components/VocabularyTableActions"
import {
  vocabularyService,
  type VocabularySearchParams,
  type VocabularyApiParams,
} from "@/services/dictionary/vocabularyApi"
import { PAGE_CONFIG, PAGINATION_CONFIG } from "@/constants/table"
import { useTableParams } from "@/hooks/useTableParams"

export const UserVocabularyPage: React.FC = () => {
  const [searchParams, setSearchParams] = useTableParams<VocabularySearchParams>({
    ...PAGE_CONFIG,
    search: "",
    reference_type: undefined,
    user_id: undefined,
  })
  const [total, setTotal] = useState<number>(0)

  const {
    data: result,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["vocabulary_list", searchParams],
    queryFn: () => {
      const apiParams: VocabularyApiParams = {
        page: searchParams.page,
        limit: searchParams.per_page,
        search: searchParams.search || undefined,
        reference_type: searchParams.reference_type || undefined,
        user_id: searchParams.user_id || undefined,
      }
      return vocabularyService.list(apiParams)
    },
  })

  useEffect(() => {
    if (result?.paginate.total) {
      setTotal(result.paginate.total)
    }
  }, [result?.paginate])

  const handleSearch = (values: Omit<VocabularySearchParams, "page" | "per_page">) => {
    setSearchParams({
      ...values,
      page: 1,
      per_page: searchParams.per_page,
    })
  }

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])

  const handleSelectionChange = (keys: React.Key[]) => {
    setSelectedRowKeys(keys)
  }

  const tableLoading = isFetching

  return (
    <div className="flex flex-col gap-2">
      <style>{`
        .text-primary {
          color: var(--ant-color-primary, #1677ff) !important;
        }
        .border-primary {
          border-color: var(--ant-color-primary, #1677ff) !important;
        }
      `}</style>
      <VocabularySearchForm
        onSearch={handleSearch}
        loading={isFetching}
        initialValues={{
          search: searchParams.search,
          reference_type: searchParams.reference_type,
          user_id: searchParams.user_id,
        }}
      />
      <Card
        className="flex min-h-0 grow flex-col"
        classNames={{ body: "flex grow" }}
        title={
          <div className="flex items-center">
            <h2>Từ vựng của người dùng</h2>
            <Divider orientation="vertical" />
            <Text type="secondary" className="text-sm!">{`${selectedRowKeys.length} mục đã được chọn`}</Text>
            <Divider orientation="vertical" />
            <VocabularyTableActions onRefresh={refetch} loading={tableLoading} />
          </div>
        }
      >
        <VocabularyTable
          data={result?.data || []}
          loading={tableLoading}
          selectedRowKeys={selectedRowKeys}
          onSelectionChange={handleSelectionChange}
          pagination={{
            pageSize: searchParams.per_page,
            current: searchParams.page,
            total,
            ...PAGINATION_CONFIG,
            onChange(page: number, pageSize: number) {
              setSearchParams({
                ...searchParams,
                page: page,
                per_page: pageSize,
              })
            },
          }}
        />
      </Card>
    </div>
  )
}

export default UserVocabularyPage
